/**
 * Send to Client Modal Component
 * KOPS-416: Modal for generating and sending deal report share links
 */

import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner, Nav, Tab } from 'react-bootstrap';
import { FaEnvelope, FaLink, FaCopy, FaCheck, FaSms, FaSearch } from 'react-icons/fa';
import axiosInstance from '../../helpers/api';
import InlineEmailComposer from '../modules/crm/InlineEmailComposer';
import { generateDealEmailTemplate, DealEmailData } from '../../utils/dealEmailTemplate';
import { generateDetailedCompAnalysisTemplate, DealCompAnalysisData } from '../../utils/dealCompAnalysisTemplate';
import { getAIEnhancedAnalysis } from '../../utils/dealAIAnalysisPrompt';

interface SendToClientModalProps {
  show: boolean;
  onHide: () => void;
  dealId: number;
  propertyAddress: string;
  dealData?: {
    listPrice?: number;
    roi?: number;
    arv?: number;
    rehabCost?: number;
    profit?: number;
    beds?: number;
    baths?: number;
    sqft?: number;
    yearBuilt?: number;
  };
}

const SendToClientModal: React.FC<SendToClientModalProps> = ({ 
  show, 
  onHide, 
  dealId,
  propertyAddress,
  dealData 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailType, setEmailType] = useState<'basic' | 'detailed' | 'ai-enhanced'>('basic');
  const [dealDetailData, setDealDetailData] = useState<any>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingAI, setLoadingAI] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState<string>('');
  const [currentEmailData, setCurrentEmailData] = useState<any>(null);

  // SMS state
  const [smsTab, setSmsTab] = useState<'email' | 'sms'>('sms');
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState<any[]>([]);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [smsSearching, setSmsSearching] = useState(false);
  const [smsSending, setSmsSending] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [smsPreview, setSmsPreview] = useState('');

  const generateSmsPreview = (contact: any) => {
    const firstName = contact?.full_name?.split(' ')[0] || 'there';
    const price = dealData?.listPrice ? `$${Number(dealData.listPrice).toLocaleString()}` : '';
    const arv = dealData?.arv ? `$${Number(dealData.arv).toLocaleString()}` : '';
    const roi = dealData?.roi ? `${dealData.roi}%` : '';
    const profit = dealData?.profit ? `$${Number(dealData.profit).toLocaleString()}` : '';
    const beds = dealData?.beds || '';
    const baths = dealData?.baths || '';
    const sqft = dealData?.sqft || '';

    let msg = `Hey ${firstName}, check out this investment opportunity:\n\n${propertyAddress}\n`;
    if (price) msg += `List: ${price}`;
    if (beds) msg += ` | ${beds}bd/${baths}ba/${sqft}sf`;
    msg += '\n';
    if (arv) msg += `ARV: ${arv}`;
    if (profit) msg += ` | Profit: ${profit}`;
    if (roi) msg += ` | ROI: ${roi}`;
    msg += '\n\nInterested? - Mike, KairosIQ Realty';
    return msg;
  };

  const handleContactSearch = async (q: string) => {
    setContactSearch(q);
    if (q.length < 2) { setContactResults([]); return; }
    setSmsSearching(true);
    try {
      const { data } = await axiosInstance.get(`/deals/contacts-search?q=${encodeURIComponent(q)}`);
      setContactResults(data.contacts || []);
    } catch { setContactResults([]); }
    finally { setSmsSearching(false); }
  };

  const handleSelectContact = async (c: any) => {
    setSelectedContact(c);
    setContactSearch(c.full_name);
    setContactResults([]);

    // Generate share link and include in preview
    let reportLink = '';
    try {
      const linkRes = await axiosInstance.post(`/deals/opportunities/${dealId}/share`, { expirationDays: 30 });
      if (linkRes.data.success) {
        reportLink = linkRes.data.data.shareUrl;
        setShareLink(reportLink);
      }
    } catch { /* link generation optional */ }

    let preview = generateSmsPreview(c);
    if (reportLink) {
      preview = preview.replace(
        '\n\nInterested?',
        `\n\nView full report: ${reportLink}\n\nInterested?`
      );
    }
    setSmsPreview(preview);
  };

  const handleSendSMS = async () => {
    if (!selectedContact) { setError('Select a contact first'); return; }
    if (!smsPreview.trim()) { setError('Message cannot be empty'); return; }
    setSmsSending(true); setError(null);
    try {
      const { data } = await axiosInstance.post('/deals/send-to-investor', {
        deal_id: dealId,
        contact_id: selectedContact.id,
        contact_name: selectedContact.full_name?.split(' ')[0] || 'there',
        contact_phone: selectedContact.phone,
        message: smsPreview,
      });
      if (data.success) { setSmsSent(true); setTimeout(() => setSmsSent(false), 3000); }
      else { setError(data.error || 'Failed to send'); }
    } catch (e: any) { setError(e.response?.data?.error || 'SMS failed'); }
    finally { setSmsSending(false); }
  };

  const handleGenerateLink = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.post(`/deals/opportunities/${dealId}/share`, {
        recipientEmail: null,
        expirationDays: 30
      });
      
      if (response.data.success) {
        setShareLink(response.data.data.shareUrl);
        return response.data.data.shareUrl;
      } else {
        throw new Error('Failed to generate share link');
      }
    } catch (err: any) {
      console.error('Error generating share link:', err);
      setError(err.response?.data?.message || 'Failed to generate share link');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchDealDetails = async () => {
    try {
      setLoadingDetail(true);
      // Fetch detailed deal data including comps
      const response = await axiosInstance.get(`/deals/opportunities/${dealId}`);
      if (response.data.success) {
        setDealDetailData(response.data.data);
        return response.data.data;
      }
      return null;
    } catch (err) {
      console.error('Error fetching deal details:', err);
      return null;
    } finally {
      setLoadingDetail(false);
    }
  };

  const handlePrepareEmail = async (type: 'basic' | 'detailed' | 'ai-enhanced' = 'basic') => {
    if (!recipientEmail) {
      setError('Please enter recipient email');
      return;
    }
    
    try {
      setLoading(type === 'basic');
      setLoadingDetail(type === 'detailed');
      setLoadingAI(type === 'ai-enhanced');
      setError(null);
      setEmailType(type);
      
      // Generate share link if not already generated
      let linkToShare = shareLink;
      if (!linkToShare) {
        linkToShare = await handleGenerateLink();
      }
      
      // If detailed or AI-enhanced analysis, fetch additional data
      if ((type === 'detailed' || type === 'ai-enhanced') && linkToShare) {
        const details = await fetchDealDetails();
        if (!details) {
          setError('Failed to fetch deal details');
          return;
        }
        
        // Store the email data for later use
        let emailData: any = {
          dealDetailData: details,
          shareLink: linkToShare,
          emailType: type
        };
        
        // If AI-enhanced, also get AI analysis
        if (type === 'ai-enhanced') {
          try {
            console.log('Fetching AI analysis for deal:', details.deal.id);
            const aiResponse = await getAIEnhancedAnalysis(details.deal, axiosInstance);
            console.log('AI Response received:', aiResponse);
            console.log('AI Response type:', typeof aiResponse);
            console.log('AI Response length:', aiResponse?.length);
            setAiAnalysis(aiResponse);
            emailData.aiAnalysis = aiResponse;
          } catch (aiErr) {
            console.error('Error getting AI analysis:', aiErr);
            // Continue without AI if it fails
            setAiAnalysis('');
            emailData.aiAnalysis = '';
          }
        }
        
        // Store the complete email data
        setCurrentEmailData(emailData);
      } else {
        // For basic email, just store minimal data
        setCurrentEmailData({
          shareLink: linkToShare,
          emailType: type,
          dealData: dealData
        });
      }
      
      if (linkToShare) {
        // Show email composer
        setShowEmailComposer(true);
      }
    } catch (err: any) {
      console.error('Error preparing email:', err);
      setError(err.response?.data?.message || 'Failed to prepare email');
    } finally {
      setLoading(false);
      setLoadingDetail(false);
      setLoadingAI(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareLink) return;
    
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleReset = () => {
    setRecipientEmail('');
    setShareLink(null);
    setError(null);
    setCopied(false);
    setShowEmailComposer(false);
    setEmailType('basic');
    setDealDetailData(null);
    setAiAnalysis('');
  };

  const handleClose = () => {
    handleReset();
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaEnvelope className="me-2" />
          Send Deal Report to Client
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Tab switcher: Email vs SMS */}
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link active={smsTab === 'sms'} onClick={() => setSmsTab('sms')}>
              <FaSms className="me-1" /> Send via Text
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link active={smsTab === 'email'} onClick={() => setSmsTab('email')}>
              <FaEnvelope className="me-1" /> Send via Email
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {/* SMS Tab */}
        {smsTab === 'sms' && (
          <div>
            <Form.Group className="mb-3">
              <Form.Label>Property</Form.Label>
              <Form.Control type="text" value={propertyAddress} disabled />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label><FaSearch className="me-1" />Search Investor Contact</Form.Label>
              <Form.Control
                type="text"
                placeholder="Type name or phone..."
                value={contactSearch}
                onChange={(e) => handleContactSearch(e.target.value)}
              />
              {smsSearching && <div className="text-muted small mt-1">Searching...</div>}
              {contactResults.length > 0 && !selectedContact && (
                <div style={{ border: '1px solid #dee2e6', borderRadius: 4, maxHeight: 200, overflowY: 'auto', marginTop: 4 }}>
                  {contactResults.map((c: any) => (
                    <div key={c.id} onClick={() => handleSelectContact(c)}
                      style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f8f9fa')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <strong>{c.full_name}</strong> <span className="text-muted">— {c.phone}</span>
                      {c.email && <span className="text-muted small ms-2">({c.email})</span>}
                    </div>
                  ))}
                </div>
              )}
              {selectedContact && (
                <Alert variant="success" className="mt-2 py-2">
                  <strong>{selectedContact.full_name}</strong> — {selectedContact.phone}
                  <Button size="sm" variant="link" className="float-end p-0" onClick={() => { setSelectedContact(null); setContactSearch(''); setSmsPreview(''); }}>Change</Button>
                </Alert>
              )}
            </Form.Group>

            {/* Message Preview — editable */}
            {selectedContact && (
              <Form.Group className="mb-3">
                <Form.Label className="d-flex justify-content-between align-items-center">
                  <span>Message Preview</span>
                  <span className="text-muted small">Edit before sending</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  value={smsPreview}
                  onChange={(e) => setSmsPreview(e.target.value)}
                  style={{ fontSize: '13px', lineHeight: 1.5 }}
                />
                <div className="text-muted small mt-1">
                  From: KairosIQ Realty (602-860-6110) • Report link will be auto-appended
                </div>
              </Form.Group>
            )}

            {/* Share link display */}
            {shareLink && selectedContact && (
              <Alert variant="info" className="py-2 mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <FaLink className="me-2" />
                    <strong>Report Link:</strong>
                    <div className="small text-break mt-1">{shareLink}</div>
                  </div>
                  <Button variant="outline-primary" size="sm" onClick={handleCopyLink}>
                    {copied ? <FaCheck /> : <FaCopy />}
                  </Button>
                </div>
              </Alert>
            )}

            <div className="d-flex gap-2 align-items-center">
              <Button variant="success" onClick={handleSendSMS} disabled={!selectedContact || !smsPreview.trim() || smsSending}>
                {smsSending ? <><Spinner animation="border" size="sm" className="me-1" />Sending...</> : <><FaSms className="me-1" />Send Text</>}
              </Button>
              {smsSent && <Alert variant="success" className="py-1 px-3 mb-0 d-flex align-items-center"><FaCheck className="me-1" /> Sent!</Alert>}
            </div>
          </div>
        )}

        {/* Email Tab (existing) */}
        {smsTab === 'email' && (<>
        {showEmailComposer && shareLink && (
          <Alert variant="info">
            <strong>Email Composer Ready</strong>
            <p className="mb-0 small">The share link has been generated. Use your email client below to send it to the recipient with a personalized message.</p>
          </Alert>
        )}

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Property</Form.Label>
            <Form.Control
              type="text"
              value={propertyAddress}
              disabled
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Recipient Email <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="email"
              placeholder="client@example.com"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              required
            />
          </Form.Group>
          
          
          {shareLink && (
            <Alert variant="info">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <FaLink className="me-2" />
                  <strong>Share Link Generated:</strong>
                  <div className="small text-break mt-1">{shareLink}</div>
                </div>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={handleCopyLink}
                >
                  {copied ? <FaCheck /> : <FaCopy />}
                </Button>
              </div>
            </Alert>
          )}
        </Form>
        </>)}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        
        {!shareLink && (
          <Button 
            variant="outline-primary" 
            onClick={handleGenerateLink}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Generating...
              </>
            ) : (
              <>
                <FaLink className="me-2" />
                Generate Link Only
              </>
            )}
          </Button>
        )}
        
        <Button 
          variant="primary" 
          onClick={() => handlePrepareEmail('basic')}
          disabled={loading || loadingDetail || !recipientEmail}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Preparing...
            </>
          ) : (
            <>
              <FaEnvelope className="me-2" />
              Prepare Email
            </>
          )}
        </Button>
        
        <Button 
          variant="success" 
          onClick={() => handlePrepareEmail('detailed')}
          disabled={loading || loadingDetail || loadingAI || !recipientEmail}
        >
          {loadingDetail ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Analyzing...
            </>
          ) : (
            <>
              <FaEnvelope className="me-2" />
              Detailed Comp Analysis
            </>
          )}
        </Button>
        
        <Button 
          variant="warning" 
          onClick={() => handlePrepareEmail('ai-enhanced')}
          disabled={loading || loadingDetail || loadingAI || !recipientEmail}
        >
          {loadingAI ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              AI Analyzing...
            </>
          ) : (
            <>
              <FaEnvelope className="me-2" />
              AI-Enhanced Analysis ✨
            </>
          )}
        </Button>
      </Modal.Footer>
      
      {/* Email Composer */}
      {showEmailComposer && shareLink && (
        <InlineEmailComposer
          lead={{
            id: dealId.toString(),
            lead_email: recipientEmail,
            lead_first_name: '',
            lead_last_name: ''
          }}
          isOpen={showEmailComposer}
          onClose={() => setShowEmailComposer(false)}
          onEmailSent={() => {
            handleReset();
            onHide();
          }}
          defaultSubject={
            emailType === 'ai-enhanced' 
              ? `🤖 AI-Enhanced Investment Analysis - ${propertyAddress}`
              : emailType === 'detailed' 
                ? `Detailed Investment Analysis with Comps - ${propertyAddress}`
                : `Investment Property Analysis - ${propertyAddress}`
          }
          defaultMessage={(() => {
            console.log('Generating email with currentEmailData:', currentEmailData);
            console.log('Email type from currentEmailData:', currentEmailData?.emailType);
            
            // Use data from currentEmailData instead of React state
            const data = currentEmailData || {};
            const emailTypeToUse = data.emailType || emailType;
            const dealDetails = data.dealDetailData;
            const aiAnalysisToUse = data.aiAnalysis;
            const shareUrlToUse = data.shareLink || shareLink;
            
            console.log('Using email type:', emailTypeToUse);
            console.log('Deal detail data available:', !!dealDetails);
            console.log('AI Analysis available:', !!aiAnalysisToUse);
            console.log('AI Analysis length:', aiAnalysisToUse?.length || 0);
            
            if ((emailTypeToUse === 'detailed' || emailTypeToUse === 'ai-enhanced') && dealDetails) {
              // Generate detailed comp analysis email
              const deal = dealDetails.deal;
              
              // Parse comp arrays from JSON
              let renovatedComps = [];
              let asIsComps = [];
              
              // Debug log to see what data we have
              console.log('Deal Detail Data:', dealDetails);
              console.log('Deal object:', deal);
              
              // Check if we have comps from the API response (backend returns 'comps' not 'comp_details')
              if (dealDetails.comps && Array.isArray(dealDetails.comps)) {
                renovatedComps = dealDetails.comps.filter((comp: any) => 
                  comp.classification === 'renovated'
                );
                asIsComps = dealDetails.comps.filter((comp: any) => 
                  comp.classification === 'as_is'
                );
                console.log('Found comps - Renovated:', renovatedComps.length, 'As-Is:', asIsComps.length);
                console.log('Sample renovated comp:', renovatedComps[0]);
                console.log('Sample as-is comp:', asIsComps[0]);
              } else if (dealDetails.comps && dealDetails.comps.length > 0) {
                // If comps don't have classification, use all as renovated for now
                console.log('Comps array found but no classification, using all as renovated comps');
                renovatedComps = dealDetails.comps;
                asIsComps = [];
              } else {
                // Fall back to parsing from deal object
                try {
                  if (deal.top_renovated_comps && typeof deal.top_renovated_comps === 'string') {
                    renovatedComps = JSON.parse(deal.top_renovated_comps);
                    console.log('Parsed renovated comps from string:', renovatedComps);
                  } else if (Array.isArray(deal.top_renovated_comps)) {
                    renovatedComps = deal.top_renovated_comps;
                    console.log('Renovated comps already array:', renovatedComps);
                  } else {
                    console.log('No renovated comps found in deal object');
                  }
                } catch (e) {
                  console.error('Error parsing renovated comps:', e, deal.top_renovated_comps);
                }
                
                try {
                  if (deal.top_as_is_comps && typeof deal.top_as_is_comps === 'string') {
                    asIsComps = JSON.parse(deal.top_as_is_comps);
                    console.log('Parsed as-is comps from string:', asIsComps);
                  } else if (Array.isArray(deal.top_as_is_comps)) {
                    asIsComps = deal.top_as_is_comps;
                    console.log('As-is comps already array:', asIsComps);
                  } else {
                    console.log('No as-is comps found in deal object');
                  }
                } catch (e) {
                  console.error('Error parsing as-is comps:', e, deal.top_as_is_comps);
                }
              }
              
              const compAnalysisData: DealCompAnalysisData = {
                propertyAddress: deal.unparsed_address || propertyAddress,
                mls_id: deal.listing_id || '',
                shareLink: shareUrlToUse,
                listPrice: deal.list_price || dealData?.listPrice || 0,
                bedrooms: deal.bedrooms,
                bathrooms: deal.bathrooms,
                sqft: deal.living_area,
                lotSize: deal.lot_size_sqft,
                yearBuilt: deal.year_built,
                daysOnMarket: deal.days_on_market,
                roi: deal.flip_roi || dealData?.roi || 0,
                arv: deal.renovated_arv || dealData?.arv || 0,
                asIsValue: deal.as_is_value,
                renoEstimate: deal.reno_estimate || 30000,
                profit: deal.flip_profit || dealData?.profit || 0,
                mao20pct: deal.mao_20pct,
                mao15pct: deal.mao_15pct,
                maoGapPct: deal.mao_gap_pct,
                investmentGrade: deal.flip_grade,
                totalComps: deal.total_comps,
                renovatedComps: renovatedComps,
                asIsComps: asIsComps,
                // TODO: Add agent info from user context
              };
              
              // For AI-enhanced, insert the AI analysis into the detailed comp analysis
              if (emailTypeToUse === 'ai-enhanced') {
                console.log('Generating AI-enhanced email. AI Analysis present:', !!aiAnalysisToUse);
                console.log('AI Analysis length:', aiAnalysisToUse?.length || 0);
                
                const baseTemplate = generateDetailedCompAnalysisTemplate(compAnalysisData);
                
                if (aiAnalysisToUse) {
                  // Insert AI analysis into the designated section
                  const aiSectionStart = baseTemplate.indexOf('<tr id="ai-analysis-section" style="display: none;">');
                  const aiSectionEnd = baseTemplate.indexOf('</tr>', aiSectionStart) + 5;
                  
                  console.log('AI section start index:', aiSectionStart);
                  console.log('AI section end index:', aiSectionEnd);
                  
                  if (aiSectionStart > -1 && aiSectionEnd > aiSectionStart) {
                    const aiSectionHtml = `
                      <tr>
                        <td style="padding: 40px 30px; border-bottom: 1px solid #e2e8f0;">
                          ${aiAnalysisToUse}
                        </td>
                      </tr>
                    `;
                    return baseTemplate.slice(0, aiSectionStart) + aiSectionHtml + baseTemplate.slice(aiSectionEnd);
                  }
                  // Fallback: insert before Investment Recommendation
                  const insertPoint = baseTemplate.indexOf('<!-- Investment Recommendation -->');
                  console.log('Investment Recommendation insert point:', insertPoint);
                  
                  if (insertPoint > -1) {
                    return baseTemplate.slice(0, insertPoint) + `<tr><td style="padding: 40px 30px;">${aiAnalysisToUse}</td></tr>` + baseTemplate.slice(insertPoint);
                  }
                }
                
                return baseTemplate;
              }
              
              return generateDetailedCompAnalysisTemplate(compAnalysisData);
            } else {
              // Generate basic email template
              const emailData: DealEmailData = {
                propertyAddress,
                shareLink: shareUrlToUse,
                listPrice: dealData?.listPrice,
                roi: dealData?.roi,
                arv: dealData?.arv,
                rehabCost: dealData?.rehabCost,
                profit: dealData?.profit,
                beds: dealData?.beds,
                baths: dealData?.baths,
                sqft: dealData?.sqft,
                yearBuilt: dealData?.yearBuilt,
                // TODO: Add agent info from user context
              };
              return generateDealEmailTemplate(emailData);
            }
          })()}
        />
      )}
    </Modal>
  );
};

export default SendToClientModal;